import { startRegistration } from '@simplewebauthn/browser'
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App,
  Button,
  Card,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Space,
  Tag,
  Typography
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { Copy, Download, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ChecklistItem } from 'src/analytics'
import {
  type Passkey,
  type TrustedDevice,
  accountPasskeysDestroyMutation,
  accountPasskeysListOptions,
  accountPasskeysListQueryKey,
  accountPasskeysRegisterStartCreate,
  accountPasskeysRegisterVerifyCreate,
  mfaConfigGenerateBackupCodesCreateMutation,
  mfaConfigRetrieveOptions,
  mfaConfigRetrieveQueryKey,
  mfaTrustedDevicesDestroyMutation,
  mfaTrustedDevicesListOptions,
  mfaTrustedDevicesListQueryKey
} from 'src/api/main'
import { client } from 'src/api/main/client.gen'
import {
  DateTimeCell,
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle
} from 'src/components'
import { getDeviceDefaultName } from 'src/features/auth/components/PasskeyRecommendationModal'
import { ChangePasswordForm } from 'src/features/profile/components'
import { useReportJourneyStep } from 'src/features/workspaces/onboarding'
import { downloadFile, useShowApiErrors } from 'src/utils'

const { Paragraph, Text } = Typography

export const AccountSecurityPanel = () => {
  const { t } = useTranslation()
  const { message } = App.useApp()
  const showApiErrors = useShowApiErrors()
  const reportStep = useReportJourneyStep()
  const queryClient = useQueryClient()
  const [passkeyForm] = Form.useForm<{ name: string }>()
  const [isPasskeyModalOpen, setIsPasskeyModalOpen] = useState(false)
  const [recoveryModalOpen, setRecoveryModalOpen] = useState(false)
  const [freshRecoveryCodes, setFreshRecoveryCodes] = useState<string[]>([])

  const invalidateSecurityQueries = async () => {
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: accountPasskeysListQueryKey()
      }),
      queryClient.invalidateQueries({
        queryKey: mfaConfigRetrieveQueryKey()
      }),
      queryClient.invalidateQueries({
        queryKey: mfaTrustedDevicesListQueryKey()
      })
    ])
  }

  const passkeysQuery = useQuery(accountPasskeysListOptions())

  const mfaConfigQuery = useQuery(mfaConfigRetrieveOptions())

  const trustedQuery = useQuery(mfaTrustedDevicesListOptions())

  const { data: mfaConfig } = mfaConfigQuery
  const passkeys = passkeysQuery.data?.results ?? []
  const trustedDevices = trustedQuery.data?.results ?? []

  const securityCenterError =
    passkeysQuery.isError || mfaConfigQuery.isError || trustedQuery.isError

  const refetchSecurityCenter = () => {
    void passkeysQuery.refetch()
    void mfaConfigQuery.refetch()
    void trustedQuery.refetch()
  }

  const { mutateAsync: addPasskey, isPending: isAddingPasskey } = useMutation({
    mutationFn: async (name: string) => {
      // Step 1: Get registration options from backend
      const { data: options } = await accountPasskeysRegisterStartCreate({
        body: { name, platform: 'web' }
      })
      // Step 2: Start WebAuthn ceremony in browser
      const credential = await startRegistration({
        optionsJSON:
          options as unknown as PublicKeyCredentialCreationOptionsJSON
      })
      // Step 3: Verify attestation on backend
      const { data: passkey } = await accountPasskeysRegisterVerifyCreate({
        body: {
          credential: credential as unknown as Record<string, unknown>,
          name,
          platform: 'web'
        }
      })

      return passkey
    },
    onSuccess: async () => {
      message.success(t('account.messages.passkeyAdded'))
      reportStep(ChecklistItem.SECURITY)
      setIsPasskeyModalOpen(false)
      passkeyForm.resetFields()
      await invalidateSecurityQueries()
    },
    onError: (e: Error) => {
      if (e.name === 'NotAllowedError') {
        message.info(t('account.messages.passkeyAborted'))
        return
      }
      if (e.name === 'InvalidStateError') {
        message.warning(t('account.messages.passkeyAlreadyRegistered'))
        void invalidateSecurityQueries()
        setIsPasskeyModalOpen(false)
        return
      }
      showApiErrors(e)
    }
  })

  const { mutateAsync: deletePasskey, isPending: isDeletingPasskey } =
    useMutation({
      ...accountPasskeysDestroyMutation(),
      onSuccess: async () => {
        message.success(t('account.messages.passkeyRemoved'))
        await invalidateSecurityQueries()
      },
      onError: (e) => showApiErrors(e)
    })

  const { mutateAsync: regenerateRecovery, isPending: isRegenerating } =
    useMutation({
      ...mfaConfigGenerateBackupCodesCreateMutation(),
      onSuccess: async (data) => {
        message.success(t('account.messages.recoveryRegenerated'))
        setFreshRecoveryCodes(data.backup_codes ?? [])
        setRecoveryModalOpen(true)
        await invalidateSecurityQueries()
      },
      onError: (e) => showApiErrors(e)
    })

  const downloadBackupCodesMutation = useMutation({
    mutationFn: async () => {
      const { data } = await client.instance.post<Blob>(
        '/mfa/config/download-backup-codes/',
        undefined,
        { responseType: 'blob' }
      )

      return data
    },
    onSuccess: (data) => {
      downloadFile(data, `recovery_codes_${Date.now()}.txt`)
    },
    onError: (e) => showApiErrors(e)
  })

  const { mutateAsync: revokeTrust, isPending: isRevokingTrust } = useMutation({
    ...mfaTrustedDevicesDestroyMutation(),
    onSuccess: async () => {
      message.success(t('account.messages.trustRevoked'))
      await invalidateSecurityQueries()
    },
    onError: (e) => showApiErrors(e)
  })

  const passkeyColumns: ColumnsType<Passkey> = [
    {
      fixed: 'left',
      title: t('account.securityCenter.passkeys.columns.name'),
      dataIndex: 'name'
    },
    {
      title: t('account.securityCenter.passkeys.columns.credential'),
      key: 'cred',
      render: (_, row) => (
        <Text type="secondary" code>
          …{row.credential_id_last_four}
        </Text>
      )
    },
    {
      title: t('account.securityCenter.passkeys.columns.platform'),
      key: 'platform',
      render: (_, row) => (
        <Tag color={row.platform === 'mobile' ? 'blue' : 'default'}>
          {row.platform === 'mobile'
            ? t('account.securityCenter.passkeys.platform.mobile')
            : t('account.securityCenter.passkeys.platform.web')}
        </Tag>
      )
    },
    {
      title: t('account.securityCenter.passkeys.columns.added'),
      key: 'created_at',
      render: (_, row) => <DateTimeCell value={row.created_at} variant="date" />
    },
    {
      title: t('account.securityCenter.passkeys.columns.lastUsed'),
      key: 'last_used_at',
      render: (_, row) => (
        <DateTimeCell value={row.last_used_at} variant="dateTime" />
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      align: 'right',
      render: (_, row) => (
        <Popconfirm
          title={t('account.securityCenter.passkeys.deleteConfirm')}
          okButtonProps={{ loading: isDeletingPasskey }}
          onConfirm={() => void deletePasskey({ path: { id: row.id } })}
        >
          <Button type="link" danger size="small">
            {t('account.securityCenter.passkeys.delete')}
          </Button>
        </Popconfirm>
      )
    }
  ]

  const trustedColumns: ColumnsType<TrustedDevice> = [
    {
      title: t('account.securityCenter.trustedDevices.columns.device'),
      key: 'device',
      fixed: 'left',
      render: (_, row) => (
        <Space vertical size={0}>
          <Text strong>
            {row.device_name ||
              t('account.securityCenter.trustedDevices.unknownDevice')}
          </Text>
          {row.ip_address ? (
            <Text type="secondary">{row.ip_address}</Text>
          ) : null}
        </Space>
      )
    },
    {
      title: t('account.securityCenter.trustedDevices.columns.trustedAt'),
      key: 'created_at',
      render: (_, row) => <DateTimeCell value={row.created_at} variant="date" />
    },
    {
      title: t('account.securityCenter.trustedDevices.columns.lastUsed'),
      key: 'last_used',
      render: (_, row) => (
        <DateTimeCell value={row.last_used} variant="dateTime" />
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      align: 'right',
      render: (_, row) => (
        <Popconfirm
          title={t('account.securityCenter.trustedDevices.revokeConfirm')}
          okButtonProps={{ loading: isRevokingTrust }}
          onConfirm={() => void revokeTrust({ path: { id: row.id } })}
        >
          <Button type="link" danger size="small">
            {t('account.securityCenter.trustedDevices.revoke')}
          </Button>
        </Popconfirm>
      )
    }
  ]

  const copyRecoveryCodes = async () => {
    await navigator.clipboard.writeText(freshRecoveryCodes.join('\n'))
    message.success(t('account.securityCenter.recovery.copied'))
  }

  return (
    <Flex vertical gap={16}>
      {securityCenterError ? (
        <QueryErrorAlert onRetry={refetchSecurityCenter} />
      ) : null}

      <ProTableContainer<Passkey>
        showSearch={false}
        rowKey="id"
        loading={passkeysQuery.isLoading}
        pagination={false}
        dataSource={passkeys}
        columns={passkeyColumns}
        locale={{
          emptyText: t('account.securityCenter.passkeys.empty')
        }}
        leftChildren={
          <Text strong>{t('account.securityCenter.passkeys.title')}</Text>
        }
        rightChildren={
          <Button
            type="primary"
            icon={<Plus size={14} />}
            onClick={() => setIsPasskeyModalOpen(true)}
          >
            {t('account.securityCenter.passkeys.add')}
          </Button>
        }
      />

      <Card
        variant="outlined"
        title={t('account.securityCenter.recovery.title')}
      >
        <Paragraph type="secondary">
          {t('account.securityCenter.recovery.description')}
        </Paragraph>
        {mfaConfig?.enabled ? (
          <Space vertical size={12} style={{ width: '100%' }}>
            {mfaConfig.backup_codes_count > 0 ? (
              <Text>
                {t('account.securityCenter.recovery.remaining', {
                  count: mfaConfig.backup_codes_count
                })}
              </Text>
            ) : (
              <Text type="secondary">
                {t('account.securityCenter.recovery.none')}
              </Text>
            )}
            <Space size={8}>
              <Popconfirm
                title={t('account.securityCenter.recovery.regenerateWarning')}
                okButtonProps={{ loading: isRegenerating }}
                onConfirm={() => void regenerateRecovery({})}
              >
                <Button danger loading={isRegenerating}>
                  {t('account.securityCenter.recovery.regenerate')}
                </Button>
              </Popconfirm>
              {mfaConfig.backup_codes_count > 0 ? (
                <Button
                  icon={<Download size={16} />}
                  loading={downloadBackupCodesMutation.isPending}
                  onClick={() => downloadBackupCodesMutation.mutate()}
                >
                  {t('account.securityCenter.recovery.download')}
                </Button>
              ) : null}
            </Space>
          </Space>
        ) : (
          <Text type="secondary">
            {t('account.securityCenter.recovery.mfaDisabled')}
          </Text>
        )}
      </Card>

      <ProTableContainer<TrustedDevice>
        showSearch={false}
        rowKey="id"
        loading={trustedQuery.isLoading}
        pagination={false}
        dataSource={trustedDevices}
        columns={trustedColumns}
        locale={{
          emptyText: t('account.securityCenter.trustedDevices.empty')
        }}
        leftChildren={
          <Text strong>{t('account.securityCenter.trustedDevices.title')}</Text>
        }
      />

      <Card
        variant="outlined"
        title={t('account.securityCenter.password.title')}
      >
        <Paragraph type="secondary">
          {t('account.securityCenter.password.description')}
        </Paragraph>
        <ChangePasswordForm />
      </Card>

      <Modal
        title={t('account.securityCenter.passkeys.addTitle')}
        open={isPasskeyModalOpen}
        okText={t('account.securityCenter.passkeys.add')}
        confirmLoading={isAddingPasskey}
        onCancel={() => setIsPasskeyModalOpen(false)}
        onOk={() => passkeyForm.submit()}
      >
        <Form
          form={passkeyForm}
          layout="vertical"
          requiredMark={false}
          initialValues={{ name: getDeviceDefaultName() }}
          onFinish={(v) => void addPasskey(v.name)}
        >
          <Form.Item
            name="name"
            label={t('account.securityCenter.passkeys.nameLabel')}
            rules={[{ required: true, message: t('required_field_error') }]}
          >
            <Input
              placeholder={t('account.securityCenter.passkeys.namePlaceholder')}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('account.securityCenter.recovery.modalTitle')}
        open={recoveryModalOpen}
        footer={[
          <Button
            key="copy"
            icon={<Copy size={16} />}
            onClick={() => void copyRecoveryCodes()}
          >
            {t('account.securityCenter.recovery.copy')}
          </Button>,
          <Button
            key="download"
            icon={<Download size={16} />}
            loading={downloadBackupCodesMutation.isPending}
            onClick={() => downloadBackupCodesMutation.mutate()}
          >
            {t('account.securityCenter.recovery.download')}
          </Button>,
          <Button
            key="close"
            type="primary"
            onClick={() => setRecoveryModalOpen(false)}
          >
            {t('account.securityCenter.recovery.done')}
          </Button>
        ]}
        onCancel={() => setRecoveryModalOpen(false)}
        width={520}
      >
        <Paragraph type="secondary">
          {t('account.securityCenter.recovery.modalDescription')}
        </Paragraph>
        <pre
          style={{
            margin: 0,
            padding: 12,
            background: 'var(--ant-color-fill-quaternary, #f5f5f5)',
            borderRadius: 8,
            fontSize: 12,
            lineHeight: 1.6,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-all'
          }}
        >
          {freshRecoveryCodes.join('\n')}
        </pre>
      </Modal>
    </Flex>
  )
}
